import { useMemo } from 'react';

import { SECTION_COMPONENTS } from 'constants/section-components';
import { DisableCandidateFormActionsProvider } from 'contexts/contexts';
import { getSectionIcon } from 'helpers/iconsHelper';
import { useCandidateApplicationUploadActions } from 'hooks/upload-actions';
import CandidatePage from 'layouts/CandidatePage/CandidatePage';
import { AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { IconComponent } from 'components/Icon/IconComponent';
import SliderSideMotion from 'components/Motion/SliderSideMotion';

import { selectCandidateApplication } from 'api/candidate-application-slice';

const CandidateSectionLayout = () => {
  const { sectionKey, token, pageIndex } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    candidateFields,
    application: { brand, candidate, other_uploads, check_values },
  } = useSelector(selectCandidateApplication);

  // Get upload handlers for candidate application
  const { getUpload, signUpload, createUpload, deleteUpload } =
    useCandidateApplicationUploadActions(token);

  // Get section data to determine page type
  const section = candidateFields?.sections?.find(s => s.code === sectionKey);
  const subsections = section?.subsections || [];
  const hasIndexPage = section?.indexPage;
  const isSinglePage = section?.singlePage;

  // Get the dynamic component for this section
  const SectionComponent = SECTION_COMPONENTS[sectionKey];

  // Create handlers object for FormPageRenderer
  const handlers = useMemo(
    () => ({
      getCandidateUpload: getUpload,
      signCandidateUpload: signUpload,
      createCandidateUpload: createUpload,
      deleteCandidateUpload: deleteUpload,
    }),
    [getUpload, signUpload, createUpload, deleteUpload],
  );

  // Simple render - let each section component handle its own logic
  const renderPage = () => {
    if (!SectionComponent) {
      return <div>{t('common.sectionNotFound')}</div>;
    }

    return (
      <SectionComponent
        candidate={candidate}
        brand={brand}
        handlers={handlers}
        token={token}
        sectionKey={sectionKey}
        pageIndex={parseInt(pageIndex) || 0}
        section={section}
        subsections={subsections}
        hasIndexPage={hasIndexPage}
        isSinglePage={isSinglePage}
        other_uploads={other_uploads}
        check_values={check_values}
        fetched={candidateFields?.sections?.length > 0}
      />
    );
  };

  // Get section translations
  const sectionTranslations = t(`candidate_fields.${sectionKey}`, { returnObjects: true });
  const title = sectionTranslations?.title || section?.title || sectionKey;
  const description = sectionTranslations?.description || section?.description || '';

  const sectionIcon = getSectionIcon(sectionKey);

  // Don't render until data is fetched
  if (!candidateFields?.sections?.length) {
    return (
      <CandidatePage token={token} withTopLogo brand={brand}>
        <CandidatePage.View>
          <CandidatePage.Card>
            <p>{t('common.loading')}</p>
          </CandidatePage.Card>
        </CandidatePage.View>
      </CandidatePage>
    );
  }

  return (
    <DisableCandidateFormActionsProvider>
      <CandidatePage token={token} withTopLogo brand={brand}>
        <CandidatePage.View>
          <CandidatePage.Card>
            {!hasIndexPage && parseInt(pageIndex) === 0 && (
              <AnimatePresence mode="wait">
                <SliderSideMotion
                  key={sectionKey}
                  delay={0.1}
                  className="d-flex flex-column u-gap-large">
                  <div className="d-flex flex-column align-items-start u-gap-large u-margin-bottom--large">
                    <IconComponent icon={sectionIcon} size="xxlarge" />
                    <div className="d-flex flex-column align-items-start u-gap-small">
                      <h1 className="primary-text u-font-weight-500 u-margin-0">{title}</h1>
                      {description && (
                        <p className="secondary-text u-font-size-medium u-font-weight-400">
                          {description}
                        </p>
                      )}
                    </div>
                  </div>
                </SliderSideMotion>
              </AnimatePresence>
            )}

            <div className="d-flex flex-column u-gap-xx-large">{renderPage()}</div>
          </CandidatePage.Card>
        </CandidatePage.View>
      </CandidatePage>
    </DisableCandidateFormActionsProvider>
  );
};

export default CandidateSectionLayout;
